'use strict';

/**
 * FieldMarkdownEditor — the rich markdown editor (pict-section-markdowneditor:
 * CodeMirror 6 + a pict-section-content preview that understands KaTeX math,
 * Mermaid diagrams and embedded images) embedded INSIDE the generic EntityList
 * edit form for any descriptor field flagged `Markdown: true` (an Issue's,
 * Vision's, Feature's, ... Description). It is the same editor as the Docs Lake
 * uses (PictView-RichDocEditor), just hosted in the form chrome instead.
 *
 * It mounts into #EntityForm-MarkdownHost, bound to AppData.FieldMarkdownEditor.Segments,
 * and on every change writes its content straight back into the form's FormValues
 * via the host (PictView-EntityList.onMarkdownChanged), so the existing save()
 * path posts the markdown with no special handling.
 *
 * Pasted / dragged / picked images are owned by the record being edited
 * (OwnerType = entity key, IDOwner = the editing id) so they live in that
 * entity's media gallery. On the create form there is no id yet, so the image
 * is uploaded owned by 0 and the host (PictView-EntityList) re-owns it to the
 * new record once it is saved. Base64 embedding is only the last resort, when
 * there is no gallery API or no owner entity at all.
 */

const libMarkdownEditor = require('pict-section-markdowneditor');

/**
 * One editor instance, addressed at a host element and a host view.
 *
 * Parameterised because there is more than one place a markdown field is edited: the generic
 * entity form, and the feature acceptance dialog. They cannot share an instance -- each carries its
 * own CodeMirror state, its own content address, and calls back into a different host -- so each
 * surface registers its own.
 */
function buildConfiguration(pOptions)
{
	let tmpOptions = pOptions || {};
	let tmpIdentifier = tmpOptions.ViewIdentifier || 'Plansheet-FieldMarkdownEditor';
	let tmpAddress = tmpOptions.HostAddress || '#EntityForm-MarkdownHost';
	let tmpDataKey = tmpOptions.DataKey || 'FieldMarkdownEditor';

	let tmpConfig = JSON.parse(JSON.stringify(libMarkdownEditor.default_configuration));
	tmpConfig.ViewIdentifier = tmpIdentifier;
	tmpConfig.DefaultDestinationAddress = tmpAddress;
	tmpConfig.TargetElementAddress = tmpAddress;
	tmpConfig.ContentDataAddress = 'AppData.' + tmpDataKey + '.Segments';
	tmpConfig.DataKey = tmpDataKey;
	// The view this editor reports its changes and its image-owner context to.
	tmpConfig.HostViewIdentifier = tmpOptions.HostViewIdentifier || 'Plansheet-EntityList';
	tmpConfig.DefaultPreviewMode = tmpOptions.PreviewMode || 'bottom';
	// Segments are a Docs Lake idea: one document made of several editable pieces. A single field
	// (an acceptance note, a Description) is one piece of prose, and offering "+ Add Segment" there
	// is a control that does nothing the person in front of it wants.
	tmpConfig.AllowSegments = (tmpOptions.AllowSegments !== false);
	tmpConfig.EnableRichPreview = true;
	tmpConfig.AutoRender = false;

	if (Array.isArray(tmpConfig.Renderables))
	{
		for (let i = 0; i < tmpConfig.Renderables.length; i++)
		{
			tmpConfig.Renderables[i].DestinationAddress = tmpAddress;
		}
	}

	return tmpConfig;
}

class PictViewFieldMarkdownEditor extends libMarkdownEditor
{
	_host() { return this.pict.views[this.options.HostViewIdentifier || 'Plansheet-EntityList']; }

	_ownerContext()
	{
		let tmpHost = this._host();
		return (tmpHost && typeof tmpHost.markdownOwnerContext === 'function') ? tmpHost.markdownOwnerContext() : { OwnerType: '', IDOwner: 0 };
	}

	/**
	 * Pasted / dragged / picked image -> upload to Parime and insert the blob URL
	 * (never a base64 data URI, which would bloat the field and the generated
	 * prompt). On the edit form the media is owned by the record straight away; on
	 * the create form there is no id yet, so it is uploaded owned by 0 and handed
	 * to the host to re-own once the record is saved. Returning true = async
	 * handling; returning false falls back to base64 (no gallery API / no owner
	 * entity, e.g. the editor hosted outside the entity form).
	 */
	onImageUpload(pFile, pSegmentIndex, fCallback)
	{
		let tmpOwner = this._ownerContext();
		let tmpMediaAPI = this.pict.providers.MediaAPI;
		if (!tmpMediaAPI || !tmpOwner.OwnerType) { return false; }
		let tmpIDOwner = tmpOwner.IDOwner || 0;
		tmpMediaAPI.upload(tmpOwner.OwnerType, tmpIDOwner, pFile)
			.then((pRow) =>
			{
				// Create form (no id yet): the media is owned by 0. Register it so the
				// host's save() re-owns it to the new record's id -> the entity gallery.
				if (!tmpIDOwner)
				{
					let tmpHost = this._host();
					if (tmpHost && typeof tmpHost.trackPendingMedia === 'function') { tmpHost.trackPendingMedia(pRow.IDMedia); }
				}
				fCallback(null, tmpMediaAPI.blobURL(pRow.IDMedia));
			})
			.catch((pErr) => fCallback(pErr.message || String(pErr)));
		return true;
	}

	// The upstream editor always renders its add-segment button. Suppressed where a field is one
	// piece of prose rather than a document.
	_renderAddButton(pContainer)
	{
		if (this.options.AllowSegments === false) { return; }
		return super._renderAddButton(pContainer);
	}

	onContentChange(pSegmentIndex, pContent)
	{
		let tmpHost = this._host();
		if (tmpHost && typeof tmpHost.onMarkdownChanged === 'function') { tmpHost.onMarkdownChanged(); }
	}
}

module.exports = PictViewFieldMarkdownEditor;
module.exports.default_configuration = buildConfiguration();
module.exports.buildConfiguration = buildConfiguration;
